// SPDX-License-Identifier: AGPL-3.0-or-later
// Pure functions that turn the dataset into ranked, human-readable findings.
// Unit-tested; no DOM.
import type { Dataset, StateRow } from './types';

export interface Insight {
  severity: 'info' | 'warn' | 'alert' | 'good';
  title: string;
  body: string;
  view?: string;
  state?: string;
}

export const median = (xs: number[]): number => {
  const a = xs.filter((x) => Number.isFinite(x)).slice().sort((x, y) => x - y);
  if (!a.length) return 0;
  const m = Math.floor(a.length / 2);
  return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
};

export const fmtInt = (n: number): string => Math.round(n).toLocaleString('en-AU');
const times = (n: number | null | undefined): string => (n == null ? '—' : `${n.toFixed(1)}×`);
const pctStr = (n: number | null | undefined, dp = 0): string => (n == null ? '—' : `${(n * 100).toFixed(dp)}%`);

/** Rank states by a numeric accessor (desc), dropping nulls. */
export function rankBy(states: StateRow[], get: (s: StateRow) => number | null): StateRow[] {
  return states
    .filter((s) => { const v = get(s); return v != null && Number.isFinite(v); })
    .slice()
    .sort((a, b) => (get(b) as number) - (get(a) as number));
}

export function buildInsights(data: Dataset): Insight[] {
  const c = data.meta.counts;
  const y0 = data.meta.firstYear;
  const yl = data.meta.latestYear;
  const out: Insight[] = [];

  out.push({
    severity: 'alert',
    title: `Aboriginal and Torres Strait Islander imprisonment is ${times(c.ratioStd)} the non-Indigenous rate`,
    body: `Age-standardised, ${fmtInt(c.atsiRateStd)} per 100,000 versus ${fmtInt(c.nonIndigRateStd)} — and the gap has widened from ${times(c.ratioStd2016)} in ${y0}. Standardising for age barely narrows it, so age structure is not what drives it.`,
    view: 'gap',
  });

  out.push({
    severity: 'warn',
    title: `More than 4 in 10 prisoners have not been sentenced`,
    body: `The remand (unsentenced) share reached ${pctStr(c.remandShare)} in ${yl}, up from ${pctStr(c.remandShare2016)} in ${y0}. Remand prisoners are awaiting trial or sentencing and are legally presumed innocent.`,
    view: 'remand',
  });

  // growth is almost entirely Aboriginal and Torres Strait Islander
  if (c.indigGrowthPct != null && c.nonIndigGrowthPct != null) {
    out.push({
      severity: 'info',
      title: `Almost all the growth since ${y0} is one group`,
      body: `Aboriginal and Torres Strait Islander prisoner numbers rose ${pctStr(c.indigGrowthPct)} since ${y0}, while the non-Indigenous count moved just ${pctStr(c.nonIndigGrowthPct)}. The total climbed ${pctStr(c.growthSince2016Pct)}.`,
      view: 'demographics',
    });
  }

  // worst over-representation ratio (age-standardised) among states
  const byRatio = rankBy(data.states, (s) => s.ratioStd);
  if (byRatio.length) {
    const top = byRatio[0];
    out.push({
      severity: 'info',
      title: `${top.name} has the widest over-representation gap`,
      body: `Its age-standardised Aboriginal and Torres Strait Islander imprisonment rate is ${times(top.ratioStd)} the non-Indigenous rate — the largest of any state or territory, even though ${top.name}'s overall rate is not the highest.`,
      view: 'gap',
      state: top.code,
    });
  }

  // highest overall rate
  const byRate = rankBy(data.states, (s) => s.rateNow);
  if (byRate.length) {
    const top = byRate[0];
    out.push({
      severity: 'info',
      title: `${top.name} imprisons the most people per head`,
      body: `${fmtInt(top.rateNow as number)} per 100,000 adults — well above the national ${fmtInt(c.rate)}. Click through to its breakdown.`,
      view: 'map',
      state: top.code,
    });
  }

  // fastest growing state
  const byGrowth = rankBy(data.states, (s) => s.growthPct);
  if (byGrowth.length) {
    const top = byGrowth[0];
    out.push({
      severity: 'warn',
      title: `${top.name}'s prison population grew fastest`,
      body: `Up ${pctStr(top.growthPct)} between ${y0} and ${yl} — from ${fmtInt(top.total[0])} to ${fmtInt(top.totalNow)} prisoners.`,
      view: 'rankings',
      state: top.code,
    });
  }

  // most remand-heavy offence
  const remandy = data.offences.divisions
    .filter((d) => d.remandShare != null && d.numberNow >= 500)
    .sort((a, b) => (b.remandShare as number) - (a.remandShare as number));
  if (remandy.length) {
    const top = remandy[0];
    out.push({
      severity: 'info',
      title: `${top.name} is the most remand-heavy major offence`,
      body: `${pctStr(top.remandShare)} of these prisoners are unsentenced — awaiting the outcome of their case rather than serving a sentence.`,
      view: 'offences',
    });
  }

  // prior imprisonment
  out.push({
    severity: 'info',
    title: `Most prisoners have been inside before`,
    body: `${pctStr(c.priorShare)} of prisoners have prior adult imprisonment — a rough measure of how often people return to custody.`,
    view: 'demographics',
  });

  return out;
}
